import { assignInlineVars } from '@vanilla-extract/dynamic';
import { type JSX, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { DataTableLoadingStateProps } from './loading-state-types.js';
import * as styles from './LoadingState.sty.js';
import { ProgressCircle } from '../../../../content/progress/ProgressCircle.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import type { TableOptions } from '../../hooks/useTable/types.js';
import { dataTableMessages } from '../../messages.js';

/** Progress circle height breakpoint (px). */
export const PROGRESS_CIRCLE_HEIGHT_BREAKPOINT = 120;

/**
 * Configuration hook for the DataTable LoadingState feature.
 * @internal
 */
export function useLoadingState<TData>(
  props: DataTableLoadingStateProps,
  options: TableOptions<TData>,
): void {
  options.state = { ...options.state, loading: props.loading };
}

/**
 * Small wrapper that makes sure to only render the defined actions when the menu
 * is open.
 * @internal
 */
export function LoadingState(
  props: Readonly<{ headerHeight: number }>,
): JSX.Element {
  const { headerHeight } = props;
  const loadingContainerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const intl = useIntl();

  useEffect(() => {
    if (!loadingContainerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        setHeight(entries[0].contentRect.height);
      }
    });
    resizeObserver.observe(loadingContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Flex
      ref={loadingContainerRef}
      justifyContent="center"
      alignItems="center"
      className={styles.loadingState}
      style={assignInlineVars({
        [styles.dataTableHeaderHeight]: `${headerHeight}px`,
      })}
    >
      <ProgressCircle
        size={
          height !== undefined && height > PROGRESS_CIRCLE_HEIGHT_BREAKPOINT
            ? 'large'
            : 'small'
        }
        aria-label={intl.formatMessage(dataTableMessages.loadingData)}
      />
    </Flex>
  );
}
